import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button, Spin, Divider, message, Table, Card, Row, Col, Statistic, Input, Tag, Tooltip, Empty, Space, Typography } from "antd";
import {
  FilePdfOutlined,
  FileExcelOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { Receipt, TrendingUp, TrendingDown, DollarSign, Download, Filter } from 'lucide-react';
import dayjs from "dayjs";
import { Method_POST_CONTEXTO } from "../../../../service/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { motion, AnimatePresence } from "framer-motion";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";
const { Title, Text } = Typography;

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);

export default function PagamentosModal({ visible, periodo, onClose, title, zIndex, onFocus }) {
  const draggableProps = useDraggableModal(zIndex, onFocus, {
    visible,
    modalKey: 'pagamentos',
    initialWidth: 1100,
    initialHeight: 'auto',
    overflow: 'hidden'
  });
  const [loading, setLoading] = useState(false);
  const [pagamentos, setPagamentos] = useState([]);
  const [searchText, setSearchText] = useState("");


  let clienteId = sessionStorage.getItem("COMPANY")
    ? JSON.parse(sessionStorage.getItem("COMPANY")).id
    : null;

  let cnpjCliente = sessionStorage.getItem("COMPANY")
    ? JSON.parse(sessionStorage.getItem("COMPANY")).cnpj
    : null;

  useEffect(() => {
    if (visible) {
      handlePesquisar();
      setSearchText(""); // Limpa a busca ao abrir
    }
  }, [visible]);

  const handlePesquisar = async () => {
    setLoading(true);
    try {
      const payload = {
        id: clienteId,
        cnpj: cnpjCliente,
        dataRef: dayjs(periodo[0]).format("YYYY-MM-DD"),
        dataVenc: dayjs(periodo[1]).format("YYYY-MM-DD"),
      };

      const response = await Method_POST_CONTEXTO(
        "/empresa/retrieve/consulta/pagamento",
        payload
      );

      if (response.erro) {
        message.error(`Erro técnico: ${response.erro.id}`);
        return;
      }

      const pagamentosOrdenados = [...response].sort(
        (a, b) =>
          dayjs(b.dataArrecadacao).valueOf() -
          dayjs(a.dataArrecadacao).valueOf()
      );

      setPagamentos(pagamentosOrdenados);
    } catch (err) {
      console.error(err);
      message.error("Erro ao consultar pagamentos.");
    } finally {
      setLoading(false);
    }
  };

  // Filtro de busca dinâmica
  const filteredData = useMemo(() => {
    return pagamentos.filter(p =>
      p?.receitaPrincipal?.codigo?.toLowerCase().includes(searchText.toLowerCase()) ||
      p?.tipo?.descricao?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [pagamentos, searchText]);

  // Cálculos do Resumo
  const totals = useMemo(() => {
    return filteredData.reduce((acc, curr) => ({
      principal: acc.principal + (curr.valorPrincipal || 0),
      encargos: acc.encargos + (curr.valorMulta || 0) + (curr.valorJuros || 0),
      total: acc.total + (curr.valorTotal || 0)
    }), { principal: 0, encargos: 0, total: 0 });
  }, [filteredData]);

  const columns = [
    {
      title: "Código",
      dataIndex: ["receitaPrincipal", "codigo"],
      key: "codigo",
      render: (text) => <Tag color="blue" style={{ fontWeight: 600 }}>{text}</Tag>
    },
    {
      title: "Descrição",
      dataIndex: ["tipo", "descricao"],
      key: "descricao",
      width: 300,
      render: (text) => (
        <Tooltip title={text}>
          <div style={{
            maxWidth: 280,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 500
          }}>
            {text}
          </div>
        </Tooltip>
      )
    },
    {
      title: "Vencimento",
      dataIndex: "dataVencimento",
      key: "dataVencimento",
      render: (v) => (
        <Space size={4}>
          <CalendarOutlined style={{ color: '#94a3b8' }} />
          <span>{v ? dayjs(v).format("DD/MM/YYYY") : "-"}</span>
        </Space>
      ),
    },
    {
      title: "Arrecadação",
      dataIndex: "dataArrecadacao",
      key: "dataArrecadacao",
      render: (v) => (
        <Space size={4}>
          <CalendarOutlined style={{ color: '#94a3b8' }} />
          <span style={{ fontWeight: 600 }}>{v ? dayjs(v).format("DD/MM/YYYY") : "-"}</span>
        </Space>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: 'center',
      render: (_, record) => {
        const isAtrasado = dayjs(record.dataArrecadacao).isAfter(dayjs(record.dataVencimento), 'day');
        return isAtrasado ? (
          <Tag icon={<ExclamationCircleOutlined />} color="error">Pago c/ Atraso</Tag>
        ) : (
          <Tag icon={<CheckCircleOutlined />} color="success">No Prazo</Tag>
        );
      }
    },
    {
      title: "Valor Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      render: (v) => <span style={{ fontWeight: 800, color: '#0f172a' }}>{formatCurrency(v)}</span>,
      align: "right",
    },
  ];

  const desmembramentoColumns = [
    { title: "Código", dataIndex: ["receitaPrincipal", "codigo"], key: "codigo" },
    { title: "Receita", dataIndex: ["receitaPrincipal", "descricao"], key: "descricao" },
    {
      title: "P. Apuração",
      dataIndex: "periodoApuracao",
      key: "periodoApuracao",
      render: (v) => (v ? dayjs(v).format("MM/YYYY") : "-"),
    },
    {
      title: "Valor Principal",
      dataIndex: "valorPrincipal",
      key: "valorPrincipal",
      render: (v) => formatCurrency(v),
      align: "right",
    },
    {
      title: "Multa/Juros",
      key: "multaJuros",
      render: (_, r) => formatCurrency((r.valorMulta || 0) + (r.valorJuros || 0)),
      align: "right",
    },
    {
      title: "Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      render: (v) => <span style={{ fontWeight: 700 }}>{formatCurrency(v)}</span>,
      align: "right",
    },
  ];

  const exportarExcel = () => {
    const dados = pagamentos.map(p => ({
      Codigo: p?.receitaPrincipal?.codigo,
      Descricao: p?.tipo?.descricao,
      "Data Vencimento": p.dataVencimento ? dayjs(p.dataVencimento).format("DD/MM/YYYY") : "",
      "Data Arrecadação": p.dataArrecadacao ? dayjs(p.dataArrecadacao).format("DD/MM/YYYY") : "",
      "Valor Principal": p.valorPrincipal,
      "Multa/Juros": (p.valorMulta || 0) + (p.valorJuros || 0),
      Total: p.valorTotal,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dados);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pagamentos");
    XLSX.writeFile(workbook, "pagamentos_financeiros.xlsx");
  };



  const exportarPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;

    const colors = {
      primary: [15, 23, 42],
      accent: [37, 99, 235],
      textMain: [30, 41, 59],
      textLight: [100, 116, 139],
      rowChild: [250, 251, 252],
      rowParent: [241, 245, 249],
      border: [226, 232, 240]
    };

    // 1. Cabeçalho
    doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    doc.rect(0, 0, pageWidth, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("RELATÓRIO DE PAGAMENTOS", 12, 18);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(160, 160, 160);
    doc.text(`EMISSÃO: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 14, 26);
    doc.text(`PERÍODO: ${dayjs(periodo[0]).format("DD/MM/YYYY")} — ${dayjs(periodo[1]).format("DD/MM/YYYY")}`, 14, 31);

    // 2. Cards de Totais
    const drawSummary = (label, value, x) => {
      doc.setFillColor(30, 41, 59);
      doc.roundedRect(x, 10, 50, 22, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184); // Cor do label
      doc.text(label.toUpperCase(), x + 5, 17);
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255); // Cor do valor
      doc.text(formatCurrency(value), x + 5, 26);
    };

    const totalGeral = pagamentos.reduce((acc, p) => acc + (p.valorTotal || 0), 0);
    const totalPrincipal = pagamentos.reduce((acc, p) => acc + (p.valorPrincipal || 0), 0);
    const totalEncargos = totalGeral - totalPrincipal;

    drawSummary("Total Principal", totalPrincipal, pageWidth - 168);
    drawSummary("Total Encargos", totalEncargos, pageWidth - 113);
    drawSummary("Total Geral", totalGeral, pageWidth - 58);

    // 3. Tabela
    const tableBody = [];
    pagamentos.forEach(p => {
      tableBody.push([
        { content: "", styles: { cellPadding: 0 } },
        { content: p?.receitaPrincipal?.codigo || "", styles: { fontStyle: 'bold' } },
        { content: p?.tipo?.descricao?.toUpperCase() || "", styles: { fontStyle: 'bold' } },
        dayjs(p.dataVencimento).format("DD/MM/YYYY"),
        dayjs(p.dataArrecadacao).format("DD/MM/YYYY"),
        { content: formatCurrency(p.valorPrincipal), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency((p.valorMulta || 0) + (p.valorJuros || 0)), styles: { halign: 'right' } },
        { content: formatCurrency(p.valorTotal), styles: { halign: 'right', fontStyle: 'bold', textColor: colors.accent } },
      ]);

      if (p.desmembramentos?.length > 0) {
        p.desmembramentos.forEach(d => {
          tableBody.push([
            "",
            { content: d?.receitaPrincipal?.codigo || "", styles: { textColor: colors.textLight, fontSize: 8 } },
            { content: d?.receitaPrincipal?.descricao || "", styles: { textColor: colors.textLight, fontSize: 8 } },
            { content: d.periodoApuracao ? `Ref: ${dayjs(d.periodoApuracao).format("MM/YYYY")}` : "", styles: { textColor: colors.textLight, fontSize: 8 } },
            "",
            { content: formatCurrency(d.valorPrincipal), styles: { halign: 'right', fontSize: 8, textColor: colors.textLight } },
            { content: formatCurrency((d.valorMulta || 0) + (d.valorJuros || 0)), styles: { halign: 'right', fontSize: 8, textColor: colors.textLight } },
            { content: formatCurrency(d.valorTotal), styles: { halign: 'right', fontSize: 8, textColor: colors.textLight } },
          ]);
        });
      }
    });

    autoTable(doc, {
      startY: 45,
      head: [["", "CÓDIGO", "DESCRIÇÃO / DETALHAMENTO", "VENCIMENTO", "PAGAMENTO", "PRINCIPAL", "ENCARGOS", "TOTAL"]],
      body: tableBody,
      theme: 'plain',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [100, 116, 139], // Slate 500
        fontSize: 8,
        fontStyle: 'bold',
      },
      styles: {
        font: "helvetica",
        fontSize: 8.5,
        cellPadding: 3.5,
      },
      columnStyles: {
        0: { cellWidth: 3 },
        1: { cellWidth: 20 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 28 },
        4: { cellWidth: 28 },
        5: { cellWidth: 28 },
        6: { cellWidth: 28 },
        7: { cellWidth: 30 },
      },
      didParseCell: (data) => {
        const isParent = data.row.cells[2].styles.fontStyle === 'bold';
        if (isParent) {
          data.cell.styles.fillColor = colors.rowParent;
          if (data.row.index !== 0) {
            data.cell.styles.lineWidth = { top: 0.5 };
            data.cell.styles.lineColor = colors.border;
          }
        } else {
          data.cell.styles.fillColor = colors.rowChild;
          if (data.column.index === 2) {
            data.cell.text = "      " + data.cell.text;
          }
        }
      },
      didDrawCell: (data) => {
        if (data.column.index === 0 && data.row.cells[2].styles.fontStyle === 'bold') {
          doc.setFillColor(colors.accent[0], colors.accent[1], colors.accent[2]);
          doc.rect(data.cell.x, data.cell.y, 1.2, data.cell.height, 'F');
        }
      }
    });

    // 4. Rodapé (CORRIGIDO)
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      // IMPORTANTE: Aqui estava o erro. Usando os valores diretos para evitar falha no spread.
      doc.setTextColor(100, 116, 139);
      doc.text(`Portal Meu Contador Online - Relatório Gerencial`, 14, doc.internal.pageSize.height - 10);
      doc.text(`Página ${i} de ${totalPages}`, pageWidth - 35, doc.internal.pageSize.height - 10);
    }

    doc.save(`Relatorio Pagamentos - ${dayjs().format("DDMMYYYY")}.pdf`);
  };

  return (
    <Modal
      title={title || "Consulta de Pagamentos"}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="excel"
          icon={<FileExcelOutlined />}
          onClick={exportarExcel}
          style={{ borderRadius: 8, fontWeight: 600 }}
        >
          Excel
        </Button>,
        <Button
          key="pdf"
          type="primary"
          icon={<FilePdfOutlined />}
          onClick={exportarPDF}
          style={{
            borderRadius: 8,
            background: '#0f172a',
            borderColor: '#0f172a',
            fontWeight: 600
          }}
        >
          Gerar PDF
        </Button>,
      ]}
      width={1400}
      {...draggableProps}
      styles={{
        body: {
          maxHeight: "80vh",
          overflowY: "auto",
          padding: '24px'
        }
      }}
    >
      <Divider style={{ margin: '15px 0' }} />

      {loading ? (
        <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 15 }}>
          <Spin size="large" />
          <Text strong style={{ color: '#64748b' }}>Sincronizando dados bancários...</Text>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Dashboard Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16, flexShrink: 0 }}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ borderRadius: 16, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <Statistic
                  title={<Text strong style={{ fontSize: 11, color: '#64748b' }}>VALOR PRINCIPAL</Text>}
                  value={totals.principal}
                  formatter={v => formatCurrency(v)}
                  valueStyle={{ color: '#0f172a', fontWeight: 900, fontSize: 16 }}
                  prefix={<DollarSign size={14} strokeWidth={3} style={{ color: '#94a3b8' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ borderRadius: 16, border: '1px solid #fecaca', background: '#fff1f2' }}>
                <Statistic
                  title={<Text strong style={{ fontSize: 11, color: '#b91c1c' }}>ENCARGOS (JUROS/MULTA)</Text>}
                  value={totals.encargos}
                  formatter={v => formatCurrency(v)}
                  valueStyle={{ color: '#b91c1c', fontWeight: 900, fontSize: 16 }}
                  prefix={<TrendingUp size={14} strokeWidth={3} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ borderRadius: 16, border: '1px solid #bbf7d0', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}>
                <Statistic
                  title={<Text strong style={{ fontSize: 11, color: '#15803d' }}>TOTAL ARRECADADO</Text>}
                  value={totals.total}
                  formatter={v => formatCurrency(v)}
                  valueStyle={{ color: '#15803d', fontWeight: 900, fontSize: 18 }}
                  prefix={<CheckCircleOutlined style={{ fontSize: 18 }} />}
                />
              </Card>
            </Col>
          </Row>

          {/* Quick Filter */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
            <Input
              placeholder="Buscar por código ou descrição..."
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ width: 350, borderRadius: 10, height: 38 }}
              allowClear
            />
            <Text style={{ color: '#64748b', fontSize: 11 }}>
              Mostrando <b>{filteredData.length}</b> lançamentos
            </Text>
          </div>

          <div style={{ flex: 1, overflow: 'hidden' }}>
            {filteredData.length > 0 ? (
              <Table
                rowKey={(r, i) => i}
                columns={columns}
                dataSource={filteredData}
                scroll={{ x: 1000 }}
                size="middle"
                expandable={{
                  expandedRowRender: (record) => (
                    <div style={{ padding: '10px 20px', background: '#f8fafc', borderRadius: 12 }}>
                      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <InfoCircleOutlined style={{ color: '#005A9E' }} />
                        <Text strong style={{ color: '#005A9E' }}>Detalhamento do Lançamento</Text>
                      </div>
                      <Table
                        rowKey={(r, i) => i}
                        columns={desmembramentoColumns}
                        dataSource={record.desmembramentos || []}
                        pagination={false}
                        size="small"
                        scroll={{ x: 700 }}
                      />
                    </div>
                  ),
                  rowExpandable: (record) =>
                    record.desmembramentos && record.desmembramentos.length > 0,
                }}
                pagination={{ pageSize: 10, size: 'small', showSizeChanger: false }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div style={{ textAlign: 'center' }}>
                    <Text strong style={{ fontSize: 16, color: '#64748b', display: 'block' }}>Nenhum pagamento encontrado</Text>
                    <Text style={{ color: '#94a3b8' }}>Tente ajustar o filtro de busca ou o período selecionado.</Text>
                  </div>
                }
                style={{ padding: 60 }}
              />
            )}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
        .premium-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #64748b !important;
          font-weight: 700 !important;
          font-size: 11px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          padding: 16px !important;
        }
        .premium-table .ant-table-tbody > tr > td {
          padding: 14px 16px !important;
        }
        .premium-table .ant-table-row:hover {
          background-color: #f1f5f9 !important;
        }
      `}} />
    </Modal>
  );
}
